import { MemoryRepositoryConfig } from '../config/repository.config.js';
import type {
  IStatsProvider,
  ISyncRepository,
} from '../interfaces/repository.interface.js';
import type { TranslationData } from '../types/translation-data.types.js';
import { createNamespaceCacheKey } from '../utils/index.js';

export class MemoryRepository
  implements ISyncRepository<MemoryRepositoryConfig>, IStatsProvider
{
  readonly name = 'MemoryRepository';
  readonly type = 'memory';
  readonly config: MemoryRepositoryConfig = {};
  private cache = new Map<string, string>();
  private accessOrder = new Map<string, number>();
  private maxSize: number = 1000;

  constructor(config: MemoryRepositoryConfig) {
    this.config = config;
    this.maxSize = this.config.options?.maxSize || 1000;
  }

  get(key: string): string | null {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Update access time for LRU
      this.accessOrder.set(key, Date.now());
      return value;
    }
    return null;
  }

  set(key: string, value: string): void {
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictOldest();
    }

    this.cache.set(key, value);
    this.accessOrder.set(key, Date.now());
  }

  remove(key: string): void {
    this.cache.delete(key);
    this.accessOrder.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder.clear();
  }

  /**
   * Clear all entries for a specific namespace
   * Removes both the namespace cache entry and all individual translation keys
   */
  clearNamespace(locale: string, namespace: string): void {
    const namespaceCacheKey = createNamespaceCacheKey(locale, namespace);
    const keyPrefix = `${locale}:${namespace}:`;

    // Remove namespace cache entry
    this.cache.delete(namespaceCacheKey);
    this.accessOrder.delete(namespaceCacheKey);

    // Remove individual translation keys
    for (const key of this.cache.keys()) {
      if (key.startsWith(keyPrefix)) {
        this.cache.delete(key);
        this.accessOrder.delete(key);
      }
    }
  }

  getNamespace(locale: string, namespace: string): TranslationData | null {
    const namespaceCacheKey = createNamespaceCacheKey(locale, namespace);
    const namespaceData = this.cache.get(namespaceCacheKey);
    if (!namespaceData) return null;

    try {
      return JSON.parse(namespaceData);
    } catch (error) {
      this.cache.delete(namespaceCacheKey);
      return null;
    }
  }

  setNamespace(
    locale: string,
    namespace: string,
    translations: TranslationData
  ): void {
    const namespaceCacheKey = createNamespaceCacheKey(locale, namespace);
    this.cache.set(namespaceCacheKey, JSON.stringify(translations));
  }

  private evictOldest(): void {
    let oldestKey = '';
    let oldestTime = Date.now();

    for (const [key, time] of this.accessOrder.entries()) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.accessOrder.delete(oldestKey);
    }
  }
  /**
   * Get all cache entries for debugging
   */
  getEntries(): Record<string, string> {
    const entries: Record<string, string> = {};
    for (const [key, value] of this.cache.entries()) {
      entries[key] = value;
    }
    return entries;
  }

  getConfig(): MemoryRepositoryConfig {
    return this.config;
  }
}
